
import { defineComponent, PropType, computed, watch } from 'vue'
import InputKin from '@/views/components/InputKin.vue'
import { corporationTypeType, CorporationTypeType } from '@/views/corporation-info/types'
import { SonekiKeisanKmkType, initializeSonekiKeisanKmk } from '@/repositories/keiei-joho/soneki-keisan/data'
import { sonekiKeisanKmkTypeType, SonekiKeisanKmkTypeType } from '@/repositories/keiei-joho/data'

export default defineComponent({
  components: { InputKin },
  props: {
    corporationType: {
      type: String as PropType<CorporationTypeType>
    },
    sonekiItems: {
      type: Array as PropType<SonekiKeisanKmkType[]>,
      required: true
    }
  },
  setup (props) {
    const sonekiItemMap = computed(() => {
      return new Map(props.sonekiItems.map(item => [item.kmkType, item]))
    })
    const getItem = (type: SonekiKeisanKmkTypeType): SonekiKeisanKmkType => {
      return sonekiItemMap.value.get(type) ?? dummyItem
    }
    const dummyItem = initializeSonekiKeisanKmk()
    dummyItem.kmkType = sonekiKeisanKmkTypeType.Dummy

    const honraiJigyoShueki = computed(() => getItem(sonekiKeisanKmkTypeType.HonraiJigyoShueki))
    const honraiJigyoHiyo = computed(() => getItem(sonekiKeisanKmkTypeType.HonraiJigyoHiyo))
    const honraiJigyohi = computed(() => getItem(sonekiKeisanKmkTypeType.HonraiJigyohi))
    const honraiHonbuhi = computed(() => getItem(sonekiKeisanKmkTypeType.HonraiHonbuhi))
    const honraiRieki = computed(() => getItem(sonekiKeisanKmkTypeType.HonraiRieki))
    const futaiJigyoShueki = computed(() => getItem(sonekiKeisanKmkTypeType.FutaiJigyoShueki))
    const futaiJigyoHiyo = computed(() => getItem(sonekiKeisanKmkTypeType.FutaiJigyoHiyo))
    const futaiRieki = computed(() => getItem(sonekiKeisanKmkTypeType.FutaiRieki))
    const shuekiJigyoShueki = computed(() => getItem(sonekiKeisanKmkTypeType.ShuekiJigyoShueki))
    const shuekiJigyoHiyo = computed(() => getItem(sonekiKeisanKmkTypeType.ShuekiJigyoHiyo))
    const shuekiRieki = computed(() => getItem(sonekiKeisanKmkTypeType.ShuekiRieki))
    const jigyoRieki = computed(() => getItem(sonekiKeisanKmkTypeType.JigyoRieki))
    const JigyoGaiShueki = computed(() => getItem(sonekiKeisanKmkTypeType.JigyoGaiShueki))
    const uketoriRisoku = computed(() => getItem(sonekiKeisanKmkTypeType.UketoriRisoku))
    const otherJigyoGaiShueki = computed(() => getItem(sonekiKeisanKmkTypeType.OtherJigyoGaiShueki))
    const jigyoGaiHiyo = computed(() => getItem(sonekiKeisanKmkTypeType.JigyoGaiHiyo))
    const shiharaiRisoku = computed(() => getItem(sonekiKeisanKmkTypeType.ShiharaiRisoku))
    const otherJigyoGaiHiyo = computed(() => getItem(sonekiKeisanKmkTypeType.OtherJigyoGaiHiyo))
    const keijoRieki = computed(() => getItem(sonekiKeisanKmkTypeType.KeijoRieki))
    const tokubetsuRieki = computed(() => getItem(sonekiKeisanKmkTypeType.TokubetsuRieki))
    const koteiShisanBaikyakuEki = computed(() => getItem(sonekiKeisanKmkTypeType.KoteiShisanBaikyakuEki))
    const otherTokubetsuRieki = computed(() => getItem(sonekiKeisanKmkTypeType.OtherTokubetsuRieki))
    const tokubetsuSonshitsu = computed(() => getItem(sonekiKeisanKmkTypeType.TokubetsuSonshitsu))
    const koteiShisanBaikyakuSon = computed(() => getItem(sonekiKeisanKmkTypeType.KoteiShisanBaikyakuSon))
    const otherTokubetsuSonShitsu = computed(() => getItem(sonekiKeisanKmkTypeType.OtherTokubetsuSonShitsu))
    const zeibikiMaeTokiJunRieki = computed(() => getItem(sonekiKeisanKmkTypeType.ZeibikiMaeTokiJunRieki))
    const hojinJuminJigyoZei = computed(() => getItem(sonekiKeisanKmkTypeType.HojinJuminJigyoZei))
    const hojinZeiChosei = computed(() => getItem(sonekiKeisanKmkTypeType.HojinZeiChosei))
    const tokiJunRieki = computed(() => getItem(sonekiKeisanKmkTypeType.TokiJunRieki))

    // 本来業務 事業利益（事業収益 - 事業費用）
    watch(() => [honraiJigyoShueki.value.kin, honraiJigyoHiyo.value.kin], ([shueki, hiyo]) => {
      honraiRieki.value.kin = (shueki ?? 0) - (hiyo ?? 0)
    })

    // 本来業務 事業費用（事業費 + 本部費）
    watch(() => [honraiJigyohi.value.kin, honraiHonbuhi.value.kin], ([jigyo, honbu]) => {
      if (props.corporationType === corporationTypeType.Type1) return
      honraiJigyoHiyo.value.kin = (jigyo ?? 0) + (honbu ?? 0)
    })

    // 附帯業務 事業利益（事業収益 - 事業費用）
    watch(() => [futaiJigyoShueki.value.kin, futaiJigyoHiyo.value.kin], ([shueki, hiyo]) => {
      futaiRieki.value.kin = (shueki ?? 0) - (hiyo ?? 0)
    })

    // 収益業務 事業利益（事業収益 - 事業費用）
    watch(() => [shuekiJigyoShueki.value.kin, shuekiJigyoHiyo.value.kin], ([shueki, hiyo]) => {
      shuekiRieki.value.kin = (shueki ?? 0) - (hiyo ?? 0)
    })

    // 事業利益（本来業務事業利益 + 附帯業務事業利益 + 収益業務事業利益）
    watch(() => [honraiRieki.value.kin, futaiRieki.value.kin, shuekiRieki.value.kin], ([honrai, futai, shueki]) => {
      jigyoRieki.value.kin = (honrai ?? 0) + (futai ?? 0) + (shueki ?? 0)
    })

    // 事業外収益（受取利息 + その他の事業外収益）
    watch(() => [uketoriRisoku.value.kin, otherJigyoGaiShueki.value.kin], ([risoku, shueki]) => {
      if (props.corporationType === corporationTypeType.Type1) return
      JigyoGaiShueki.value.kin = (risoku ?? 0) + (shueki ?? 0)
    })

    // 事業外費用（支払利息 + その他の事業外費用）
    watch(() => [shiharaiRisoku.value.kin, otherJigyoGaiHiyo.value.kin], ([risoku, hiyo]) => {
      if (props.corporationType === corporationTypeType.Type1) return
      jigyoGaiHiyo.value.kin = (risoku ?? 0) + (hiyo ?? 0)
    })

    // 経常利益（事業利益 + 事業外収益 - 事業外費用）
    watch(() => [jigyoRieki.value.kin, JigyoGaiShueki.value.kin, jigyoGaiHiyo.value.kin], ([rieki, shueki, hiyo]) => {
      keijoRieki.value.kin = (rieki ?? 0) + (shueki ?? 0) - (hiyo ?? 0)
    })

    // 特別利益（固定資産売却益 + その他の特別利益）
    watch(() => [koteiShisanBaikyakuEki.value.kin, otherTokubetsuRieki.value.kin], ([baikyakuEki, rieki]) => {
      if (props.corporationType === corporationTypeType.Type1) return
      tokubetsuRieki.value.kin = (baikyakuEki ?? 0) + (rieki ?? 0)
    })

    // 特別損失（固定資産売却損 + その他の特別損失）
    watch(() => [koteiShisanBaikyakuSon.value.kin, otherTokubetsuSonShitsu.value.kin], ([baikyakuSon, sonshitsu]) => {
      if (props.corporationType === corporationTypeType.Type1) return
      tokubetsuSonshitsu.value.kin = (baikyakuSon ?? 0) + (sonshitsu ?? 0)
    })

    // 税引前当期純利益（経常利益 + 特別利益 - 特別損失）
    watch(() => [keijoRieki.value.kin, tokubetsuRieki.value.kin, tokubetsuSonshitsu.value.kin], ([keijo, rieki, sonshitsu]) => {
      zeibikiMaeTokiJunRieki.value.kin = (keijo ?? 0) + (rieki ?? 0) - (sonshitsu ?? 0)
    })

    // 当期純利益（税引前当期純利益 - 法人税・住民税及び事業税 - 法人税等調整額）
    watch(() => [zeibikiMaeTokiJunRieki.value.kin, hojinJuminJigyoZei.value.kin, hojinZeiChosei.value.kin], ([rieki, jigyo, chosei]) => {
      tokiJunRieki.value.kin = (rieki ?? 0) - (jigyo ?? 0) - (chosei ?? 0)
    })

    /**
     * 科目項目の入力可否を法人タイプに応じて判定する
     */
    const isDisabledByCorporationType = (item: SonekiKeisanKmkType): boolean => {
      if (props.corporationType === corporationTypeType.Type1) {
        return !item.isRequiredSettingType1
      } else {
        return !item.isRequiredSettingType2To5
      }
    }

    /**
     * 金額が正か負かで「利益」または「損失」の文字列を返す
     */
    const getRiekiOrSonsitsuLabel = (kin: number): string => {
      return kin >= 0 ? '利益' : '損失'
    }

    return {
      sonekiKeisanKmkTypeType,
      honraiJigyoShueki,
      honraiJigyoHiyo,
      honraiJigyohi,
      honraiHonbuhi,
      honraiRieki,
      futaiJigyoShueki,
      futaiJigyoHiyo,
      futaiRieki,
      shuekiJigyoShueki,
      shuekiJigyoHiyo,
      shuekiRieki,
      jigyoRieki,
      JigyoGaiShueki,
      uketoriRisoku,
      otherJigyoGaiShueki,
      jigyoGaiHiyo,
      shiharaiRisoku,
      otherJigyoGaiHiyo,
      keijoRieki,
      tokubetsuRieki,
      koteiShisanBaikyakuEki,
      otherTokubetsuRieki,
      tokubetsuSonshitsu,
      koteiShisanBaikyakuSon,
      otherTokubetsuSonShitsu,
      zeibikiMaeTokiJunRieki,
      hojinJuminJigyoZei,
      hojinZeiChosei,
      tokiJunRieki,
      isDisabledByCorporationType,
      getRiekiOrSonsitsuLabel
    }
  }
})
